import { useMemo } from 'react'

import { useBookkeepingBankAccounts } from '@features/accounts/hooks/useBookkeepingBankAccounts'
import { useHasBookkeepingAccountData } from '@features/accounts/hooks/useHasBookkeepingAccountData'
import { useOpenBankingAccounts } from '@features/openBanking/hooks/useOpenBankingAccounts'
import { OpenBankingAccount } from '@features/openBanking/types/openBankingAccount'
import { isOpenBankingAccountDeleted } from '@features/openBanking/utils/isOpenBankingAccountDeleted'
import { Account } from '@modules-deprecated/app/accounts/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useCurrencies } from '../../../hooks/useCurrencies'
import { isKontistAccount } from '../../../utils/accountTypeCheckers'
import { BankAccountSource } from '../enums/bankAccountSource'
import { useFetchBankAccounts } from '../hooks/useFetchBankAccounts'
import { useHasConnectedBankAccount } from '../hooks/useHasConnectedBankAccount'
import { BankAccount } from '../types/bankAccount'
import { getAccountsTotalBalance } from '../utils/getAccountsTotalBalance'
import { sortBankAccounts } from '../utils/sortBankAccounts'

interface BankAccountListData {
  activeAccounts: Account[] | undefined
  bankAccountsByAccountId: Map<string, BankAccount>
  openBankingAccountsByAccountId: Map<string | null, OpenBankingAccount>
  totalBalance: number
  baseCurrencyId: string
  hasOnlyEmptyDefaultBankAccount: boolean
  hasConnectedBankAccount: boolean
  shouldShowBankUpsell: boolean
}

export const useBankAccountListData = (): BankAccountListData => {
  const { bookkeepingBankAccounts: accounts } = useBookkeepingBankAccounts()
  const { bankAccounts } = useFetchBankAccounts()
  const { openBankingAccounts } = useOpenBankingAccounts({ withDeleted: true })
  const { data: currenciesData } = useCurrencies()
  const { organization } = useUserOrganization()
  const { hasConnectedBankAccount } = useHasConnectedBankAccount()

  const baseCurrencyId = organization?.baseCurrencyId || 'EUR'

  const bankAccountsByAccountId = useMemo(
    () => new Map(bankAccounts?.map((ba) => [ba.bookkeepingAccountId, ba])),
    [bankAccounts],
  )

  const openBankingAccountsByAccountId = useMemo(
    () => new Map(openBankingAccounts?.map((oba) => [oba.externalAccountId, oba])),
    [openBankingAccounts],
  )

  const activeAccounts = useMemo(() => {
    const filteredAccounts = accounts?.filter((account) => !account.isArchived)

    return filteredAccounts
      ? sortBankAccounts({ accounts: filteredAccounts, bankAccounts, openBankingAccounts })
      : undefined
  }, [accounts, bankAccounts, openBankingAccounts])

  const accountsToCalculateBalance = useMemo(() => {
    if (!activeAccounts) {
      return []
    }

    return activeAccounts.filter((account) => {
      const openBankingAccount = openBankingAccountsByAccountId.get(account.id)

      return !openBankingAccount || !isOpenBankingAccountDeleted(openBankingAccount)
    })
  }, [activeAccounts, openBankingAccountsByAccountId])

  const totalBalance = useMemo(
    () =>
      getAccountsTotalBalance({
        activeAccounts: accountsToCalculateBalance,
        bankAccountsByAccountId,
        currencies: currenciesData?.currencies,
        baseCurrencyId,
      }),
    [accountsToCalculateBalance, bankAccountsByAccountId, currenciesData?.currencies, baseCurrencyId],
  )

  const isSingleAccount = activeAccounts?.length === 1
  const singleAccount = isSingleAccount ? activeAccounts[0] : undefined
  const isSingleAccountConnected =
    singleAccount &&
    (bankAccountsByAccountId.get(singleAccount.id)?.source !== BankAccountSource.BILLY ||
      openBankingAccountsByAccountId.has(singleAccount.id) ||
      isKontistAccount(singleAccount))

  const { bookkeepingAccountHasExistingData: singleAccountHasData } = useHasBookkeepingAccountData({
    selectedBookkeepingAccount: isSingleAccountConnected ? undefined : singleAccount,
  })
  const hasOnlyEmptyDefaultBankAccount = isSingleAccount && !singleAccountHasData && !isSingleAccountConnected

  const hasKontistAccount = !!activeAccounts?.some(isKontistAccount)

  const shouldShowBankUpsell =
    !!activeAccounts?.length && !hasConnectedBankAccount && !hasKontistAccount && !hasOnlyEmptyDefaultBankAccount

  return {
    activeAccounts,
    bankAccountsByAccountId,
    openBankingAccountsByAccountId,
    totalBalance,
    baseCurrencyId,
    hasOnlyEmptyDefaultBankAccount,
    hasConnectedBankAccount,
    shouldShowBankUpsell,
  }
}
